import type { Role, Validator } from '@servicetim/shared';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from '@servicetim/ui/components';
import { Plus, Trash2 } from 'lucide-react';
import type { FieldCatalog } from '../../projects/catalog/field-catalog';
import { FieldConditionRow, fieldOptions } from '../../projects/conditions/field-condition-row';
import { removeAt, replaceAt } from '../../projects/lib/list-utils';
import { SimpleSelect } from '../../projects/ui/simple-select';

export interface ValidatorsEditorProps {
  value: readonly Validator[];
  onChange: (next: Validator[]) => void;
  catalog: FieldCatalog;
  roles: readonly Role[];
  idPrefix: string;
}

type ValidatorType = Validator['type'];
const LABELS: Record<ValidatorType, string> = {
  field_required: 'Field must have a value',
  field_matches: 'Field must match',
  no_open_children: 'No open child tickets',
  approval_required: 'Approval required',
};

/**
 * `roles` defaults to `[]` for callers that only need the other validator types; a fresh
 * `approval_required` then starts with an empty `roleId` (invalid until the user picks a role),
 * mirroring `newLeaf('user_in_group', …)` in `condition-builder.tsx`.
 */
export function newValidator(
  type: ValidatorType,
  catalog: FieldCatalog,
  roles: readonly Role[] = [],
): Validator {
  const firstField = catalog.custom[0]?.key ?? 'summary';
  switch (type) {
    case 'field_required':
      return { type, field: firstField };
    case 'field_matches':
      return { type, field: firstField, op: 'eq' };
    case 'no_open_children':
      return { type };
    case 'approval_required':
      return { type, approvers: { kind: 'role', roleId: roles[0]?.id ?? '' }, mode: 'all' };
  }
}

/** Ordered list of validators (checked against the ticket + submitted values; failure = 400). */
export function ValidatorsEditor({
  value,
  onChange,
  catalog,
  roles,
  idPrefix,
}: ValidatorsEditorProps) {
  const update = (index: number, next: Validator) => onChange(replaceAt(value, index, next));
  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-fg-muted text-sm">
          No validators: the transition only needs its conditions.
        </p>
      ) : (
        <ul className="space-y-2">
          {value.map((v, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; their position is the key
              key={`${idPrefix}-${index}-${v.type}`}
              className="space-y-2 rounded-md border border-border bg-surface-raised p-3"
            >
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{LABELS[v.type]}</Badge>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-auto"
                  aria-label={`Remove validator ${index + 1}`}
                  onClick={() => onChange(removeAt(value, index))}
                >
                  <Trash2 />
                </Button>
              </div>
              {v.type === 'field_required' ? (
                <SimpleSelect
                  aria-label="Field"
                  size="sm"
                  className="w-64"
                  value={v.field}
                  options={fieldOptions(catalog)}
                  onChange={(next) => next && update(index, { ...v, field: next })}
                />
              ) : null}
              {v.type === 'field_matches' ? (
                <FieldConditionRow
                  idPrefix={`${idPrefix}-${index}`}
                  value={{ field: v.field, op: v.op, value: v.value }}
                  catalog={catalog}
                  allowMe={false}
                  onChange={(next) =>
                    update(index, {
                      ...v,
                      field: next.field,
                      op: next.op,
                      ...(next.value === undefined ? { value: undefined } : { value: next.value }),
                    })
                  }
                />
              ) : null}
              {v.type === 'approval_required' ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <span className="font-medium text-fg-muted text-xs">Approver role</span>
                    {/* v1: role-based approvers only; user/group/field specs (ADR-0019 §3)
                        are valid JSON but have no picker here yet. */}
                    <SimpleSelect
                      aria-label="Approver role"
                      size="sm"
                      value={v.approvers.kind === 'role' ? v.approvers.roleId : null}
                      options={roles.map((r) => ({ value: r.id, label: r.name }))}
                      onChange={(next) =>
                        next && update(index, { ...v, approvers: { kind: 'role', roleId: next } })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <span className="font-medium text-fg-muted text-xs">Approval mode</span>
                    <SimpleSelect
                      aria-label="Approval mode"
                      size="sm"
                      value={v.mode}
                      options={[
                        { value: 'all', label: 'All approvers must approve' },
                        { value: 'any', label: 'Any one approver' },
                      ]}
                      onChange={(next) =>
                        next && update(index, { ...v, mode: next as 'all' | 'any' })
                      }
                    />
                  </div>
                </div>
              ) : null}
              <Input
                aria-label="Message shown when the validator fails"
                placeholder="Message shown when this fails (optional)"
                value={v.message ?? ''}
                maxLength={200}
                className="h-8 text-xs"
                onChange={(e) =>
                  update(index, {
                    ...v,
                    message: e.target.value === '' ? undefined : e.target.value,
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={value.length >= 20}>
            <Plus />
            Add validator
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {(Object.keys(LABELS) as ValidatorType[]).map((type) => (
            <DropdownMenuItem
              key={type}
              onSelect={() => onChange([...value, newValidator(type, catalog, roles)])}
            >
              {LABELS[type]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
